/** Pinia store for editor UI state — panels, dialogs, flags. */
import { defineStore } from 'pinia';
import { CANVAS_VIEWS } from '@/constants/canvasView';
import type { CanvasView } from '@/types/ui/canvasView';
import { DEFAULT_GRID_ZOOM } from '@/constants/attributeGrid';
import type { LayerHighlight, UiState } from '@/types/stores/ui';

export const useUiStore = defineStore('ui', {
  state: (): UiState => ({
    variantGridOpen: false,
    sidebarVisible: true,
    appNavOpen: false,
    showSaveDialog: false,
    showUnsavedChangesDialog: false,
    hasSavedInSession: false,
    canvasView: CANVAS_VIEWS.MOSAIC,
    desiredIsCore: null,
    activePanelKey: null,
    showExportDialog: false,
    showExportSummaryDialog: false,
    timelineOpen: false,
    timelineZoom: 50,
    variantGridZoom: DEFAULT_GRID_ZOOM,
    selectedBlockId: null,
    highlight: null,
    expandedTrackIds: [],
    animationHiddenIds: [],
    isQuickExporting: false,
    isCanvasViewportMoving: false,
    canvasRetinaEnabled: true,
    liveTextEdits: {},
    translationErrors: {}
  }),
  actions: {
    setLiveText(layerId: string, text: string) {
      this.liveTextEdits[layerId] = text;
    },
    clearLiveText(layerId: string) {
      delete this.liveTextEdits[layerId];
    },
    setTranslationError(
      variantId: string,
      attributeId: string,
      message: string
    ) {
      const row = this.translationErrors[variantId] ?? {};
      row[attributeId] = message;
      this.translationErrors[variantId] = row;
    },
    clearTranslationError(variantId: string, attributeId: string) {
      const row = this.translationErrors[variantId];
      if (!row) return;
      delete row[attributeId];
      if (Object.keys(row).length === 0)
        delete this.translationErrors[variantId];
    },
    clearTranslationErrors() {
      this.translationErrors = {};
    },
    toggleVariantGrid() {
      this.variantGridOpen = !this.variantGridOpen;
    },
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    toggleAppNav() {
      this.appNavOpen = !this.appNavOpen;
    },
    setCanvasView(view: CanvasView) {
      this.canvasView = view;
    },
    openPanel(key: string) {
      this.sidebarVisible = true;
      this.activePanelKey = key;
    },
    closePanel() {
      this.activePanelKey = null;
    },
    togglePanel(key: string) {
      if (this.activePanelKey === key) {
        this.closePanel();
        return;
      }
      this.openPanel(key);
    },
    toggleTimeline() {
      this.timelineOpen = !this.timelineOpen;
    },
    setSelectedBlock(id: string | null) {
      this.selectedBlockId = id;
    },
    /** Use `highlightLayers` / `clearHighlight` in selectionService. */
    setHighlight(highlight: LayerHighlight | null) {
      if (!highlight && !this.highlight) return;
      this.highlight = highlight;
    },
    setTrackExpanded(layerId: string, open: boolean) {
      const isOpen = this.expandedTrackIds.includes(layerId);
      if (open === isOpen) return;
      this.expandedTrackIds = open
        ? [...this.expandedTrackIds, layerId]
        : this.expandedTrackIds.filter(id => id !== layerId);
    },
    setAnimationHidden(layerId: string, hidden: boolean) {
      const isHidden = this.animationHiddenIds.includes(layerId);
      if (hidden === isHidden) return;
      this.animationHiddenIds = hidden
        ? [...this.animationHiddenIds, layerId]
        : this.animationHiddenIds.filter(id => id !== layerId);
    },
    resetUi() {
      this.variantGridOpen = false;
      this.showSaveDialog = false;
      this.showUnsavedChangesDialog = false;
      this.hasSavedInSession = false;
      this.canvasView = CANVAS_VIEWS.MOSAIC;
      this.desiredIsCore = null;
      this.activePanelKey = null;
      this.timelineOpen = false;
      this.timelineZoom = 50;
      this.variantGridZoom = DEFAULT_GRID_ZOOM;
      this.selectedBlockId = null;
      this.highlight = null;
      this.expandedTrackIds = [];
      this.animationHiddenIds = [];
      this.isQuickExporting = false;
      this.isCanvasViewportMoving = false;
      this.canvasRetinaEnabled = true;
      this.translationErrors = {};
      this.showExportDialog = false;
      this.showExportSummaryDialog = false;
    }
  }
});
